/**
 * RealisticFire — particle-based fire animation on <canvas>.
 *
 * Renders on a fully transparent background (no border, no dark gradient),
 * so the flames appear to burn directly over whatever sits behind the
 * widget. Trail fade uses `destination-out` so previous frames are erased
 * from the alpha channel instead of being painted over with opaque black.
 *
 * Performance: hard-capped particle count, requestAnimationFrame, and
 * device-pixel-ratio clamped to 2. Stops drawing if tab is hidden.
 */
import { useEffect, useRef } from "react";

type Particle = {
  x: number; y: number; vx: number; vy: number;
  life: number; max: number; size: number; hueShift: number;
};

export function RealisticFire({ height = 64 }: { height?: number }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = wrap.clientWidth;
    let h = height;

    const setup = () => {
      w = wrap.clientWidth;
      h = height;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setup();

    const ro = new ResizeObserver(setup);
    ro.observe(wrap);

    const particles: Particle[] = [];
    const MAX = 140;
    const SPAWN_PER_FRAME = 5;

    const spawn = () => {
      const x = Math.random() * w;
      const center = w / 2;
      const pull = (center - x) * 0.002;
      const max = 600 + Math.random() * 500;
      particles.push({
        x,
        y: h + 2,
        vx: (Math.random() - 0.5) * 0.4 + pull,
        vy: -(0.7 + Math.random() * 1.2),
        life: 0,
        max,
        size: 4 + Math.random() * 6,
        hueShift: (Math.random() - 0.5) * 8,
      });
    };

    let raf = 0;
    let last = performance.now();
    let visible = true;
    const onVis = () => { visible = !document.hidden; if (visible) last = performance.now(); };
    document.addEventListener("visibilitychange", onVis);

    const draw = (now: number) => {
      raf = requestAnimationFrame(draw);
      if (!visible) return;
      const dt = Math.min(48, now - last);
      last = now;

      for (let i = 0; i < SPAWN_PER_FRAME && particles.length < MAX; i++) spawn();

      // Trail fade on transparent background: erase alpha each frame so the
      // canvas behind the flames stays see-through.
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.28)";
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = "lighter";
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;
        if (p.life >= p.max) { particles.splice(i, 1); continue; }
        p.vx += Math.sin((p.life + p.x) * 0.01) * 0.02;
        p.vy -= 0.012;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        p.size *= 0.992;

        const t = p.life / p.max;
        let r: number, g: number, b: number, a: number;
        if (t < 0.15) {
          r = 255; g = 240; b = 200; a = 0.95;
        } else if (t < 0.45) {
          const k = (t - 0.15) / 0.30;
          r = 255; g = 200 - k * 80; b = 80 - k * 60; a = 0.85;
        } else if (t < 0.8) {
          const k = (t - 0.45) / 0.35;
          r = 255 - k * 60; g = 120 - k * 90; b = 30 - k * 20; a = 0.65 - k * 0.3;
        } else {
          const k = (t - 0.8) / 0.2;
          r = 60 - k * 30; g = 60 - k * 30; b = 60 - k * 30; a = 0.25 * (1 - k);
        }
        const rad = Math.max(0.5, p.size);
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
        grd.addColorStop(0, `rgba(${r|0},${g|0},${b|0},${a})`);
        grd.addColorStop(1, `rgba(${r|0},${g|0},${b|0},0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [height]);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className="relative mt-1.5 w-full overflow-hidden"
      style={{ height, background: "transparent" }}
    >
      <canvas ref={canvasRef} className="block w-full h-full bg-transparent" />
    </div>
  );
}
